import { Button, Stack, Typography, Box } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MiddleCard from "../../components/cards/MiddleCard";
import { PasswordInput, TextInput } from "../../components/inputs";
import SubmitButton from "../../components/SubmitButton";
import { useAuth } from "../../contexts/AuthContext";
import { useLogin } from "../../contexts/LoginContext";
import {
  PasswordRecoveryConfirmationData,
  PasswordRecoveryData,
  passwordRecoverySchema,
  passwordRecoveryConfirmationSchema,
} from "./types";
import { ZodSchema } from "zod";
import { useError } from "../../contexts/ErrorContext";
import MiddleFormCard from "../../components/cards/MiddleFormCard";
import { useApp } from "../../contexts/AppContext";
import { useApi } from "../../contexts/ApiContext";

type Schemas = ZodSchema<
  PasswordRecoveryData | PasswordRecoveryConfirmationData
>;
type StepType = "code" | "new_pass" | "email_sent" | "done";
const PasswordRecovery = () => {
  const { email, setEmail, maskedEmail } = useLogin();

  const { resetPassword, confirmResetPassword } = useAuth();

  const [formErrors, setFormErrors] = useState<any>([]);
  const [newPassword, setNewPassword] = useState("");
  const [code, setCode] = useState("");
  const [username, setUsername] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [validateOnChange, setValidateOnChange] = useState<boolean>(false);
  const [step, setStep] = useState<StepType>("code");
  const navigate = useNavigate();
  const { search } = useLocation();
  const { showError } = useError();

  const { translate } = useApp();
  const authTranslations = useMemo(() => translate("auth"), [translate]);
  const { setSubmitting } = useApi();

  useEffect(() => {
    const params = new URLSearchParams(search);
    const qCode = params.get("c");
    const username = params.get("n");
    if (qCode?.length && username?.length) {
      setStep("new_pass");
      setCode(qCode);
      setUsername(username);
    }
  }, []);

  const validate = () => {
    const schema = schemaByStep();
    if (!schema) {
      return false;
    }

    const result = schema.safeParse(dataByStep());

    if (!result.success) {
      const errorDetails = result.error.flatten().fieldErrors;
      setFormErrors(errorDetails);
      return false;
    } else {
      setFormErrors({});
    }
    return true;
  };

  const dataByStep = ():
    | PasswordRecoveryData
    | PasswordRecoveryConfirmationData => {
    if (step === "new_pass") {
      return {
        username,
        code,
        newPassword,
        newPasswordConfirm,
      } as PasswordRecoveryConfirmationData;
    }
    return { email } as PasswordRecoveryData;
  };

  const schemaByStep = (): Schemas | null => {
    switch (step) {
      case "code":
        return passwordRecoverySchema;
      case "new_pass":
        return passwordRecoveryConfirmationSchema;
    }
    return null;
  };

  const handlePasswordRecovery = async () => {
    setValidateOnChange(true);
    if (!validate()) {
      return;
    }
    try {
      setSubmitting(true);
      if (step === "new_pass") {
        await confirmResetPassword(username, code, newPassword);
        setStep("done");
      }
      if (step === "code") {
        await resetPassword(email);
        setStep("email_sent");
      }
    } catch (error: any) {
      showError(
        `${authTranslations.errors.error_resetting_password}: ${error?.message}`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const backToLogin = () => {
    navigate("/login");
  };

  useEffect(() => {
    if (validateOnChange) {
      validate();
    }
  }, [email, newPassword, newPasswordConfirm]);

  return (
    <React.Fragment>
      {step === "code" && (
        <MiddleFormCard
          title={authTranslations.password_reset.title}
          subtitle={authTranslations.password_reset.subtitle}
          onSubmit={handlePasswordRecovery}
        >
          <Stack gap={1}>
            <TextInput
              error={!!formErrors.email}
              helperText={formErrors.email}
              label={authTranslations.password_reset.fields.email}
              placeholder={authTranslations.password_reset.fields.email}
              value={email}
              onChange={(value: number | string) => setEmail(value as string)}
            />
            <Stack justifyContent={"flex-end"} gap={1} direction={"row"}>
              <Button
                onClick={() => navigate("/login", { replace: true })}
                color="whiteButton"
              >
                {authTranslations.password_reset.buttons.login}
              </Button>
              <SubmitButton
                fullWidth={false}
                color={"blackButton"}
                onClick={handlePasswordRecovery}
              >
                {authTranslations.password_reset.buttons.submit}
              </SubmitButton>
            </Stack>
          </Stack>
        </MiddleFormCard>
      )}
      {step === "email_sent" && (
        <MiddleCard>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {authTranslations.password_reset.check_email}
          </Typography>
          <Typography textAlign={"left"} variant={"caption"}>
            {authTranslations.password_reset.check_email_subtitle.replace(
              "{maskedEmail}",
              maskedEmail
            )}
          </Typography>
          <Box textAlign={"right"}>
            <SubmitButton
              fullWidth={false}
              color={"blackButton"}
              onClick={backToLogin}
            >
              {authTranslations.password_reset.buttons.ok}
            </SubmitButton>
          </Box>
        </MiddleCard>
      )}
      {step === "new_pass" && (
        <MiddleFormCard
          title={"Reset Your Password"}
          onSubmit={handlePasswordRecovery}
        >
          <Stack gap={1}>
            <TextInput
              error={!!formErrors.code}
              helperText={formErrors.code}
              label="Code"
              placeholder=""
              value={code}
              disabled
              onChange={() => {}}
            />
            <PasswordInput
              label={authTranslations.password_reset.fields.new_password}
              value={newPassword}
              onChange={(value: string) => setNewPassword(value)}
              helperText={translate(
                "auth",
                `form.errors.${formErrors.newPassword}`
              )}
              error={!!formErrors.newPassword}
            />
            <TextInput
              type="password"
              error={!!formErrors.newPasswordConfirm}
              helperText={formErrors.newPasswordConfirm}
              label={authTranslations.password_reset.fields.confirm_password}
              placeholder=""
              value={newPasswordConfirm}
              onChange={(value: number | string) =>
                setNewPasswordConfirm(value as string)
              }
            />
            <Stack justifyContent={"flex-end"} gap={1} direction={"row"}>
              <SubmitButton
                fullWidth={false}
                color={"blackButton"}
                onClick={handlePasswordRecovery}
              >
                {authTranslations.password_reset.buttons.submit_reset}
              </SubmitButton>
            </Stack>
          </Stack>
        </MiddleFormCard>
      )}

      {step === "done" && (
        <MiddleCard>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {authTranslations.password_reset.done.title}
          </Typography>
          <Typography textAlign={"left"} variant={"caption"}>
            {authTranslations.password_reset.done.subtitle}
          </Typography>
          <Box mt={3} textAlign={"right"}>
            <SubmitButton
              fullWidth={false}
              color={"blackButton"}
              onClick={backToLogin}
            >
              {authTranslations.password_reset.buttons.login}
            </SubmitButton>
          </Box>
        </MiddleCard>
      )}
    </React.Fragment>
  );
};

export default PasswordRecovery;
